<template>
  <div class="wrapper">
    <div class="animated fadeIn">
      <b-card>
        <b-row>
          <b-col>
            <b-form-group>
              <b-input-group>
                <b-form-input :placeholder="$t('global.search')" v-model="filters.search"></b-form-input>
                <b-input-group-append>
                  <b-button variant="primary" @click="getData()"><i class="fa fa-search" /></b-button>
                </b-input-group-append>
              </b-input-group>
            </b-form-group>
          </b-col>
          <b-col md="auto">
            <b-button-group>
              <b-button :pressed="isListView" @click="isListView = true"><i class="fa fa-list"></i></b-button>
              <b-button :pressed="!isListView" @click="isListView = false"><i class="fa fa-th"></i></b-button>
            </b-button-group>
          </b-col>
          <b-col md="auto">
            <b-form-group label-cols :label="$dt('global.per_page', 'Per page:')">
              <b-form-select :options="[2, 10, 20, 50, 100, 500]" v-model="filters.per_page" />
            </b-form-group>
          </b-col>
          <b-col md="auto" v-if="listPaginator.last_page > 1">
            <Pagination :data="listPaginator" :limit="5" @pagination-change-page="onPageChange" />
          </b-col>
        </b-row>

        <b-card bg-variant="light" class="mb-2">
          <b-row>
            <b-col>
              <div>
                From
                <DatePicker
                  v-model="filters.from_ts"
                  type="datetime"
                  format="DD.MM.YYYY HH:mm:ss"
                  value-type="timestamp"
                  :placeholder="$t('time_logs.filters.from_ts')"
                />
              </div>
              <div>
                To
                <DatePicker
                  v-model="filters.to_ts"
                  format="DD.MM.YYYY HH:mm:ss"
                  type="datetime"
                  value-type="timestamp"
                  :placeholder="$t('time_logs.filters.to_ts')"
                />
              </div>
            </b-col>
            <b-col>
              <div class="tags-container">
              <b-form-group class="mb-0">
                <label class="tag_label">{{ $dt('label__set_samples_list.tags_filter', 'Tags') }}</label>
                <template v-if="!shouldHideAdvancedFilters">
                  <input type="checkbox" class="checkbox" id="checkbox" v-model="advanced_search" value="true"
                    unchecked-value="false">
                  <label class="checkbox_label" for="checkbox">Advanced search </label>
                </template>
              </b-form-group>
              <used-tags-popup :label_set_id="label_set_id" />
              </div>
              <b-form-group>
                <TagSelect v-show="!shouldHideAdvancedFilters" :placeholder="$dt('label__set_samples_list.placeholders.tags', 'Universial tags')"
                  config="async" :delayed="false" :custom="{ multiple: true }" v-model="filters.tags" />
                <TagSelect v-show="advanced_search && !shouldHideAdvancedFilters"
                  :placeholder="$dt('label__set_samples_list.placeholders.label_object_tags', 'Label object tags')"
                  config="async" :delayed="false" :custom="{ multiple: true }" v-model="filters.labelObjectTags" />
                <TagSelect v-show="advanced_search && !shouldHideAdvancedFilters"
                  :placeholder="$dt('label__set_samples_list.placeholders.sample_tags', 'Sample tags')" config="async"
                  :delayed="false" :custom="{ multiple: true }" v-model="filters.sampleTags" />
                <TagSelect v-show="advanced_search || shouldHideAdvancedFilters"
                  :placeholder="$dt('label__set_samples_list.placeholders.label_object_set_tags', 'Label object set tags')"
                  config="async" :delayed="false" :custom="{ multiple: true }" v-model="filters.labelObjectSetTags" />
              </b-form-group>
            </b-col>
            <b-col>
              <div>
                {{ $dt('label__set_samples_list.filter_by_verified', 'Filter by verified') }}
                <b-form-select v-model="filters.verified" :options="verifiedOptions"></b-form-select>
              </div>
              <div>
                {{ $dt('label__set_samples_list.filter_by_tagged', 'Filter by tagged') }}
                <b-form-select v-model="filters.tagged" :options="taggedOptions"></b-form-select>
              </div>
            </b-col>
          </b-row>
        </b-card>

        <b-card bg-variant="light" class="mb-2">
          <b-row>
            <b-col>
              <div class="mb-2 d-flex align-items-center justify-content-between">
                <div>
                  {{ selectedSamples.length }} Samples selected
                </div>
                <b-btn class="w-50" block @click="selectAll">
                  {{ $dt('label__set_samples_list.select_all', 'Select all') }}
                </b-btn>
              </div>
              <b-btn :disabled="!selectedSamples.length" block variant="danger" @click="selectedSamples = [];">
                {{ $dt('label__set_samples_list.clear_selection_button', 'Clear selections') }}
              </b-btn>
              <b-button-group class="d-flex mt-2">
                <b-btn variant="primary" :disabled="!selectedSamples.length" @click="setVerifiedSamples">
                  {{ $dt('label__set_samples_list.set_selection_verified', 'Set selected verified') }}
                </b-btn>
                <b-btn variant="warning" :disabled="!selectedSamples.length" @click="setVerifiedSamples(false)">
                  {{ $dt('label__set_samples_list.set_selection_not_verified', 'Mark as not verified') }}
                </b-btn>
              </b-button-group>
            </b-col>
            <b-col>
              <b-form-group :label="$dt('label__set_samples_list.tags', 'Tags')" class="mb-0">
                <TagSelect :disabled="!selectedSamples.length"
                  :placeholder="$t('label__set_samples_list.placeholders.tags')" config="async"
                  :custom="{ multiple: true }" :delayed="false" v-model="tags4Selection" />
                <b-button-group class="d-flex mt-2">
                  <b-btn v-if="tags4Selection.length && selectedSamples.length" variant="primary" class=""
                    @click="applyTags(false)">
                    {{ $dt('label__set_samples_list.apply_tags_for_selection_add', 'Add tags to selected') }}
                  </b-btn>
                  <b-btn v-if="tags4Selection.length && selectedSamples.length" variant="warning" class=""
                    @click="applyTags(true)">
                    {{ $dt('label__set_samples_list.apply_tags_for_selection_remove', 'Remove tags from selected') }}
                  </b-btn>
                </b-button-group>
              </b-form-group>
            </b-col>
            <b-col>
              <div>
                <span>{{$dt('label__set_samples_list.total_samples_filtered', 'Total samples filtered')}}: </span>
                <span>
                  {{list.meta.total}}
                  <span v-if="list.meta.total !== list.meta.total_samples_in_date_range">
                    ({{ Math.round((list.meta.total / list.meta.total_samples_in_date_range) * 100) }}% of {{list.meta.total_samples_in_date_range}})
                  </span>
                </span>
              </div>
            </b-col>
          </b-row>
        </b-card>
        <div v-if="isListView">
          <NoDataView v-if="!tableItems.length" />
          <LabelSetSamplesListView v-else :loading="loading" :label_set_id="this.label_set_id"
            :filtersSortOrder="filters.sort_order" :filtersSortField="filters.sort_field" :labelSetSamples="tableItems"
            :selectedSamples="selectedSamples" :queryParams="params" @toggleSelectSample="toggleSelectSample"
            @changeFilters="changeFilters" />
        </div>
        <div v-else>
          <div v-if="loading" class="text-center">
            <b-spinner type="grow" variant="primary" />
          </div>
          <NoDataView v-else-if="!tableItems.length" />
          <LabelSetSamplesThumbnailView v-else :labelSetSamples="tableItems" :label_set_id="this.label_set_id"
            :queryParams="params" :selectedSamples="selectedSamples" @toggleSelectSample="toggleSelectSample" />
        </div>

        <!--Paginator at bottom-->
        <div v-if="tableItems.length">
          <Pagination :data="listPaginator" :limit="5" @pagination-change-page="onPageChange" />
        </div>
      </b-card>
    </div>
  </div>
</template>

<script>
import Pagination from 'laravel-vue-pagination';
import { mapState } from 'vuex';
import DatePicker from 'vue2-datepicker';
import LabelSetSamplesListView from '@/views/LabelSets/LabelSetSamplesListView.vue';
import LabelSetSamplesThumbnailView from '@/views/LabelSets/LabelSetSamplesThumbnailView.vue';
import NoDataView from '@/views/Shared/NoDataView.vue';
import Services from '@/services/index';
import { fileIconMap } from '@/shared/classes';
import MD5 from '@/shared/md5';
import '@riophae/vue-treeselect/dist/vue-treeselect.css';
import TagSelect from '@/views/Shared/TagSelect';
import UsedTagsPopup from '@/views/Shared/UsedTagsPopup.vue';

export default {
  mounted() {
    this.selectedSamples = [];
    this.getAllItemsInfo(true);
    this.getData();
    this.getInfoData();
  },
  props: {
    page: Number,
    label_set_id: {
      type: Number,
      required: true
    },
  },
  data() {
    return {
      advanced_search: false,
      verifiedOptions: [
        { value: null, text: this.$t('sample.all_filter_by_verified') },
        { value: 0, text: this.$t('sample.only_un_verified_filter') },
        { value: 1, text: this.$t('sample.only_verified_filter') },
      ],
      taggedOptions: [
        { value: null, text: this.$t('sample.all_filter_by_tagged') },
        { value: 0, text: this.$t('sample.only_un_tagged_filter') },
        { value: 1, text: this.$t('sample.only_tagged_filter') },
      ],
      fileIconMap,
      filters: {
        sort_field: 'id',
        sort_order: true,
        search: '',
        per_page: 20,
        tag: null,
        tags: [],
        labelObjectTags: [],
        sampleTags: [],
        labelObjectSetTags: [],
        tagged: null,
        verified: null,
        light_weight: false,
      },

      loading: false,
      loadingListInfo: false,
      list: {},
      listInfo: {}, // List with all pages
      listInfoIds: [], // List with all ids of all samples
      listInfoParamsHash: null, // List with all pages requested with following params (hash of params)

      labelSet: null,
      tagFilter: null,
      selectedSamples: [],
      lastSelectedSample: null,
      lastSelectedSampleUnselected: false,
      tags4Selection: [],
      isListView: true,
    };
  },
  components: {
    DatePicker,
    LabelSetSamplesListView,
    LabelSetSamplesThumbnailView,
    NoDataView,
    Pagination,
    TagSelect,
    UsedTagsPopup,
  },
  computed: {
    listPaginator() {
      return Object.is(this.list.meta, undefined) ? {} : this.list.meta;
    },
    tableItems() {
      return this.list.data || [];
    },
    params() {
      return {
        ...this.filters,
        from_ts: this.normalizePickerTimestamp(this.filters.from_ts),
        to_ts: this.normalizePickerTimestamp(this.filters.to_ts),
        page: this.page,
        with_activities: true,
        sort_order: this.filters.sort_order ? 'desc' : 'asc'
      };
    },
    ...mapState('Auth', ['activeProjectId']),
    shouldHideAdvancedFilters() {
      const hideAdvancedFilters = window.ENV.VUE_APP_LABEL_SET_SAMPLES_HIDE_ADVANCED_FILTERS;
      return hideAdvancedFilters === true || hideAdvancedFilters === 'true' || hideAdvancedFilters === 'TRUE';
    },
  },
  watch: {
    page: 'getData',
    filters: { deep: true, handler: 'getData' },
  },
  methods: {
    normalizePickerTimestamp(timestamp) {
      if (timestamp == null) return timestamp;

      // vue2-datepicker returns the selected wall-clock time as a UTC timestamp.
      return timestamp + (new Date(timestamp).getTimezoneOffset() * 60 * 1000);
    },
    changeFilters(changedFilters) {
      this.filters.sort_order = changedFilters.sort_order;
      this.filters.sort_field = changedFilters.sort_field;
    },
    toggleSelectSample(sampleId, event) {
      if (event.shiftKey && this.lastSelectedSample) {
        const fromIdx = this.listInfoIds.findIndex((e) => e === this.lastSelectedSample);
        const toIdx = this.listInfoIds.findIndex((e) => e === sampleId);
        if (fromIdx >= 0 && toIdx >= 0) {
          const idArray = this.listInfoIds
            .filter((e, idx) => (idx >= Math.min(fromIdx, toIdx)) && (idx <= Math.max(fromIdx, toIdx)));
          if (this.lastSelectedSampleUnselected) {
            // remove from selection besause firs in range was unselected
            this.selectedSamples = this.selectedSamples.filter((e) => !idArray.includes(e));
          } else {
            // add to selection
            this.selectedSamples = [...new Set([...this.selectedSamples, ...idArray])];
          }
        }
        this.lastSelectedSample = sampleId;
      } else if (this.selectedSamples.includes(sampleId)) {
        this.selectedSamples = this.selectedSamples.filter((e) => e !== sampleId);
        this.lastSelectedSample = sampleId;
        this.lastSelectedSampleUnselected = true;
      } else {
        this.selectedSamples = [...this.selectedSamples, sampleId];

        this.lastSelectedSample = sampleId;
        this.lastSelectedSampleUnselected = false;
      }
    },
    applyTags(remove_tags = false) {
      const samples = [...new Set(this.selectedSamples)];
      const tags = [...new Set(this.tags4Selection)];

      if (!samples.length || !tags.length) return;
      Services.labelSets.applyTags(this.activeProjectId, this.label_set_id, { tags, samples, remove_tags }).then(() => {
        this.getData();
      });
    },
    setVerifiedSamples(verified) {
      if (verified !== false) verified = true;
      const samples = [...new Set(this.selectedSamples)];
      if (!samples.length) return;
      Services.labelSets.setVerifiedSamples(this.activeProjectId, this.label_set_id, { verified, samples }).then(() => {
        this.getData();
      });
    },
    getInfoData() {
      Services.labelSets.editData(this.activeProjectId, this.label_set_id).then(({ data }) => {
        this.labelSet = data.labelSet;
      });
    },
    getData() {
      this.loading = true;
      this.getAllItemsInfo();
      Services.labelSets.sampleList(this.activeProjectId, this.label_set_id, this.params).then(({ data }) => {
        if (this.page > data.meta.last_page) {
          this.onPageChange(data.meta.last_page);
          return;
        }
        this.list = data;
        this.list.data = this.list.data.map((s) => ({
          ...s,
          label_object_set_last_updated: (s.label_object_sets || []).reduce((max, e) => ((max < e.updated_at) ? e.updated_at : max), null),
          activities: (s.label_object_sets || []).reduce((allActivities, ls) => allActivities.concat(ls.last_activities || []), [])
        }));
        this.loading = false;
      }).catch((err) => { this.loading = false; console.log(err); });
    },
    // Method for selecting all and other purposes, gets all items paginated
    getAllItemsInfo(force = false) {
      const params = {
        ...this.params, light_weight: true, notpaginate: 1, page: null
      };
      const listInfoParamsHash = MD5(JSON.stringify(params)).toString();
      if (!force && this.listInfoParamsHash === listInfoParamsHash) return;
      this.listInfoParamsHash = listInfoParamsHash;
      this.loadingListInfo = true;
      Services.labelSets.sampleList(this.activeProjectId, this.label_set_id, {
        ...this.params, light_weight: true, notpaginate: 1, page: null
      }).then(({ data }) => {
        if (listInfoParamsHash !== this.listInfoParamsHash) return;
        this.listInfo = data;
        this.listInfoIds = (data.data || []).map((e) => e.id);
        this.loadingListInfo = false;
      }).catch((err) => { this.loadingListInfo = false; });
    },
    onPageChange(page) {
      page = page || this.page;
      if (page !== this.page) this.$router.push({ name: 'LabelSetSamples', params: { page } });
    },
    selectAll() {
      this.selectedSamples = this.list.data.map((e) => e.id);
    }
  }
};
</script>

<style  lang="scss">
.tab-content.hide-tab {
  display: none;
}

.tag_label {
  align-content: left;
  padding: 0 30px 0 0;
}

.checkbox_label {
  padding: 0 100px 0 5px;
}
.checkbox {
  margin-bottom: 0.5rem;
}

.tags-container {
  display: flex;
}
</style>
